import { useEffect, useRef, useState } from 'react';
import {
  INTERVALLE_LECTURE_MS,
  MESSAGE_CAMERA_REFUSEE,
  MESSAGE_SANS_CAMERA,
  creerDetecteur,
  scanCameraDisponible,
} from '../lib/code-barres';
import { C } from '../theme/palette';
import { FS } from '../theme/typo';

/**
 * Bouton « 📷 Scanner avec la caméra » : ouvre la caméra arrière, lit le
 * premier code-barres reconnu et le rend par `onCode`.
 *
 * <p>Sans `BarcodeDetector`, le bouton reste visible mais affiche un message
 * clair : la saisie au clavier ou à la douchette, à côté, fait le même travail.
 *
 * @param {object} props propriétés du composant
 * @param {(code: string) => void} props.onCode reçoit le code lu
 * @param {string} [props.label] libellé du bouton
 * @param {boolean} [props.compact] simple pastille 📷 à poser au bout du champ
 *   code-barres, pour garder la mise en page de la maquette, qui n'a pas de
 *   bouton caméra
 * @returns {import('react').ReactElement} le bouton et son écran de lecture
 */
export function ScanCodeBarres({ onCode, label, compact }) {
  const [ouvert, setOuvert] = useState(false);
  const [message, setMessage] = useState('');
  const videoRef = useRef(null);
  const fluxRef = useRef(null);
  const minuterieRef = useRef(null);
  const actifRef = useRef(false);

  const arreter = () => {
    actifRef.current = false;
    clearTimeout(minuterieRef.current);
    try {
      fluxRef.current?.getTracks().forEach((t) => t.stop());
    } catch {
      // Flux déjà fermé.
    }
    fluxRef.current = null;
    setOuvert(false);
  };

  // Caméra libérée si l'écran se ferme pendant la lecture.
  useEffect(() => () => arreter(), []);

  const demarrer = async () => {
    if (!scanCameraDisponible()) {
      setMessage(MESSAGE_SANS_CAMERA);
      return;
    }
    setMessage('');
    setOuvert(true);
    actifRef.current = true;
    try {
      const detecteur = await creerDetecteur();
      const flux = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      fluxRef.current = flux;
      if (!actifRef.current) return arreter();
      const video = videoRef.current;
      if (video) {
        video.srcObject = flux;
        await video.play();
      }
      const lire = async () => {
        if (!actifRef.current) return;
        try {
          const trouves = await detecteur.detect(videoRef.current);
          const code = trouves?.find((t) => t.rawValue)?.rawValue;
          if (code && actifRef.current) {
            arreter();
            onCode && onCode(String(code).trim());
            return;
          }
        } catch {
          // Image pas encore prête : on réessaie au tour suivant.
        }
        minuterieRef.current = setTimeout(lire, INTERVALLE_LECTURE_MS);
      };
      lire();
    } catch {
      arreter();
      setMessage(MESSAGE_CAMERA_REFUSEE);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={demarrer}
        aria-label={compact ? 'Scanner avec la caméra' : undefined}
        title={compact ? 'Scanner avec la caméra' : undefined}
        style={
          compact
            ? {
                flexShrink: 0,
                width: 40,
                height: 38,
                border: '1px solid ' + C.border,
                background: C.white,
                color: C.teal,
                borderRadius: 9,
                fontSize: 14,
                cursor: 'pointer',
                marginBottom: 10,
              }
            : {
                width: '100%',
                border: '1px dashed ' + C.teal,
                background: C.white,
                color: C.teal,
                borderRadius: 9,
                padding: '10px',
                fontSize: FS.sm,
                fontWeight: 700,
                cursor: 'pointer',
                minHeight: 44,
                marginBottom: message ? 4 : 10,
              }
        }
      >
        {compact ? '📷' : label || '📷 Scanner avec la caméra'}
      </button>
      {message && (
        <div
          role="status"
          style={{
            fontSize: FS.xs,
            color: C.amber,
            marginBottom: 10,
            ...(compact ? { flexBasis: '100%', marginTop: -6 } : {}),
          }}
        >
          {message}
        </div>
      )}
      {ouvert && (
        <div
          role="dialog"
          aria-label="Lecture du code-barres"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.92)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            paddingTop: 'max(16px, var(--st-haut))',
            paddingBottom: 'max(16px, env(safe-area-inset-bottom, 0px))',
          }}
        >
          <video
            ref={videoRef}
            playsInline
            muted
            style={{ width: '100%', maxWidth: 440, borderRadius: 14, background: '#000' }}
          />
          <div style={{ color: '#fff', fontSize: FS.sm, marginTop: 12, textAlign: 'center' }}>
            Placez le code-barres dans le cadre : il est lu tout seul.
          </div>
          <button
            type="button"
            onClick={arreter}
            style={{
              marginTop: 16,
              background: '#fff',
              color: C.ink,
              border: 'none',
              borderRadius: 9,
              padding: '10px 18px',
              fontWeight: 800,
              minHeight: 44,
              cursor: 'pointer',
            }}
          >
            Annuler
          </button>
        </div>
      )}
    </>
  );
}
